// spellPersistence.js
// Combat PRO - Custom Spell cloud persistence runtime.
//
// Stage 51 (Custom Spell Persistence Runtime - portal integration).
//
// Owns the runtime that loads and saves user-created custom spells against the
// portal backend, plus the load + normalise + merge composition. It does NOT
// own any UI, editor state, modal state, or React rendering. React (via
// CombatContext) consumes this runtime; it does not duplicate it.
//
// Portal integration:
//   The legacy battletracker.html used standalone auth (API_BASE + authFetch +
//   getAuthHeaders + bt_auth_token + credentials:'include'). NONE of that is
//   recreated here. Combat PRO runs inside the authenticated VISINVOKE customer
//   portal, whose request layer is the shared axios instance in src/api.js
//   (baseURL + HttpOnly auth_token cookie via withCredentials, plus the 401/403
//   interceptor). This module reuses that instance exactly. The only thing
//   defined here is the endpoint PATH, which mirrors the legacy contract
//   (API_BASE was https://api.visinvoke.com/v1/battletracker, endpoint
//   /user/spells), so the full path under the portal baseURL is
//   /v1/battletracker/user/spells.
//
// Merge / model logic is NOT duplicated: normalisation and dedup-by-name reuse
// the Stage 50 foundation helpers (normalizeUserSpells, mergeSpellLists).
//
// Deferred (not this stage): editor / modal / CRUD / validation UI / progress /
// preview / import / export / recommendation / combat / execution changes, and
// the legacy vi_rebuildSpellSelects select-refresh (UI). There is no spell
// DELETE / PUT / import / export in the legacy implementation; none is invented.

import API from '../../api';
import { normalizeUserSpells, mergeSpellLists } from './spellStudioEngine';

// Endpoint path under the portal API baseURL. Mirrors the legacy
// API_BASE ('/v1/battletracker') + '/user/spells'. Auth and base URL are owned
// by the shared API instance; this is only the route.
export var USER_SPELLS_PATH = '/v1/battletracker/user/spells';

// loadUserSpells - GET the current user's saved custom spells.
// Verbatim-faithful to legacy fetchUserSpells: returns the list of spell data
// objects, mapping each persisted row to its .data payload; returns [] on any
// failure (the portal interceptor handles 401/403 before this catch). The
// shared API sends the auth cookie automatically (withCredentials).
export async function loadUserSpells() {
    try {
        var res = await API.get(USER_SPELLS_PATH);
        var rows = res ? res.data : null;
        if (!Array.isArray(rows)) { return []; }
        return rows.map(function(s) { return s ? s.data : undefined; });
    } catch (err) {
        return [];
    }
}

// saveUserSpell - POST a single custom spell.
// Verbatim-faithful to legacy saveUserSpell: body { name, data }, name falling
// back to 'Custom Spell'. Returns true on a 2xx response, false on any failure.
// axios resolves only on 2xx by default and sets JSON Content-Type for object
// bodies, so no manual header is needed.
export async function saveUserSpell(spellData) {
    try {
        var name = (spellData && spellData.name) ? spellData.name : 'Custom Spell';
        var res = await API.post(USER_SPELLS_PATH, { name: name, data: spellData });
        return !!(res && res.status >= 200 && res.status < 300);
    } catch (err) {
        return false;
    }
}

// loadAndMergeUserSpells - boot/load composition.
// Mirrors the legacy boot path (fetchUserSpells then vi_mergeCustomSpells) minus
// the deferred vi_rebuildSpellSelects UI. Composes ONLY the Stage 50 helpers:
//   - normalizeUserSpells: drop nameless entries, ensure id + source 'user'
//     (preserves existing custom spell IDs).
//   - mergeSpellLists: append user spells into the base list by unique name
//     (avoids duplicates, preserves system spells).
// Returns the normalised user spells and the merged runtime spell DB. It does
// NOT touch window or React state; the caller (CombatContext) applies them.
//
// options: { spellsDb } - the current runtime spell DB (the base to merge into).
export async function loadAndMergeUserSpells(options) {
    var opts = options || {};
    var baseDb = Array.isArray(opts.spellsDb) ? opts.spellsDb : [];
    var raw = await loadUserSpells();
    var userSpells = normalizeUserSpells(raw);
    var mergedDb = mergeSpellLists(baseDb, userSpells);
    return { userSpells: userSpells, mergedDb: mergedDb };
}
